import React from 'react';
import { motion } from 'framer-motion';
interface ArtifactCardProps {
  title: string;
  description: string;
  microcopy: string;
  price?: string;
  signColor: string;
  isBundle?: boolean;
  /** SVG sigil node rendered inside the sacred seal */
  sigil?: React.ReactNode;
}
/**
 * Sealed mythological archive card.
 * Communicates "hidden archive waiting to awaken" — premium, cinematic,
 * intentionally sealed (NOT broken or disabled).
 */
export function ArtifactCard({
  title,
  description,
  microcopy,
  price,
  signColor,
  isBundle = false,
  sigil
}: ArtifactCardProps) {
  return <motion.div className="group relative rounded-xl overflow-hidden h-full" whileHover={{
    scale: 1.015
  }} transition={{
    type: 'spring',
    stiffness: 200,
    damping: 20
  }} style={{
    // Sealed atmospheric base — deep, mysterious, never gray
    background: `linear-gradient(155deg, rgba(12,12,20,0.96) 0%, ${signColor}10 50%, rgba(8,8,14,0.98) 100%)`
  }} data-id="element-288">
      {/* Ornamental border (double-line, metallic fantasy) */}
      <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
      border: `1px solid ${signColor}40`,
      boxShadow: `inset 0 0 0 1px ${signColor}15, 0 0 18px ${signColor}15`
    }} data-id="element-289" />
      {/* Pulsing sacred glow */}
      <motion.div className="absolute inset-0 rounded-xl pointer-events-none" animate={{
      opacity: [0.25, 0.55, 0.25]
    }} transition={{
      duration: 4.5,
      repeat: Infinity,
      ease: 'easeInOut'
    }} style={{
      boxShadow: `inset 0 0 60px ${signColor}25`
    }} data-id="element-290" />

      {/* Soft atmospheric smoke — drifting */}
      <motion.div className="absolute inset-0 pointer-events-none opacity-40" animate={{
      backgroundPosition: ['0% 0%', '100% 100%']
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: 'linear'
    }} style={{
      background: `radial-gradient(ellipse at 20% 30%, ${signColor}25 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, ${signColor}15 0%, transparent 50%)`,
      backgroundSize: '200% 200%',
      filter: 'blur(20px)'
    }} data-id="element-291" />

      {/* Floating dust particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl" data-id="element-292">
        {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute w-[2px] h-[2px] rounded-full" style={{
        backgroundColor: signColor,
        left: `${15 + i * 14}%`,
        top: `${20 + i % 3 * 25}%`,
        boxShadow: `0 0 6px ${signColor}`
      }} animate={{
        y: [-8, -28, -8],
        opacity: [0.2, 0.7, 0.2]
      }} transition={{
        duration: 5 + i * 0.6,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.4
      }} data-id="element-293" />)}
      </div>

      {/* DESBLOQUEO FUTURO capsule (top-right) */}
      <div className="absolute top-4 right-4 z-20" data-id="element-294">
        <motion.div className="relative px-3 py-1.5 rounded-full backdrop-blur-sm" style={{
        background: `linear-gradient(135deg, rgba(20,20,28,0.85), ${signColor}20)`,
        border: `1px solid ${signColor}70`,
        boxShadow: `0 0 14px ${signColor}50, inset 0 0 8px ${signColor}20`
      }} animate={{
        boxShadow: [`0 0 14px ${signColor}40, inset 0 0 8px ${signColor}15`, `0 0 22px ${signColor}70, inset 0 0 12px ${signColor}30`, `0 0 14px ${signColor}40, inset 0 0 8px ${signColor}15`]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-295">
          <span className="text-[10px] font-serif tracking-[0.18em] uppercase font-semibold" style={{
          color: signColor,
          textShadow: `0 0 8px ${signColor}80`
        }} data-id="element-296">
            Desbloqueo Futuro
          </span>
        </motion.div>
      </div>

      {/* Subtle sealed-state veil — readable, not disabled */}
      <div className="absolute inset-0 pointer-events-none rounded-xl" style={{
      background: 'linear-gradient(180deg, rgba(5,5,8,0.15) 0%, rgba(5,5,8,0.35) 100%)'
    }} data-id="element-297" />

      {/* Content */}
      <div className="relative z-10 p-6 md:p-7 flex flex-col items-center text-center min-h-[340px]" data-id="element-298">
        {/* Sacred sigil */}
        <motion.div className="relative mb-5 mt-2" animate={{
        rotate: 360
      }} transition={{
        duration: 60,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-299">
          <motion.div className="absolute inset-0 rounded-full" animate={{
          scale: [1, 1.15, 1],
          opacity: [0.4, 0.8, 0.4]
        }} transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} style={{
          boxShadow: `0 0 30px ${signColor}80, 0 0 60px ${signColor}40`
        }} data-id="element-300" />
          <div className="relative w-20 h-20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-500" style={{
          background: `radial-gradient(circle, ${signColor}30 0%, transparent 70%)`,
          border: `1px solid ${signColor}60`
        }} data-id="element-301">
            {sigil ? <div style={{
            color: signColor
          }} data-id="element-302">
                {sigil}
              </div> : <DefaultSigil color={signColor} data-id="element-303" />}
          </div>
        </motion.div>

        {/* Title */}
        <h3 className={`text-xl md:text-2xl font-serif font-semibold mb-3 ${isBundle ? 'tracking-wide' : ''}`} style={{
        color: isBundle ? signColor : '#F5F0E1',
        textShadow: `0 2px 12px rgba(0,0,0,0.7), 0 0 20px ${signColor}30`
      }} data-id="element-304">
          {title}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-300/90 leading-relaxed mb-4 max-w-sm" data-id="element-305">
          {description}
        </p>

        {/* Microcopy — sealed flavor text */}
        <p className="text-xs italic font-serif leading-relaxed mb-5 max-w-xs" style={{
        color: `${signColor}CC`,
        textShadow: `0 0 10px ${signColor}30`
      }} data-id="element-306">
          {microcopy}
        </p>

        {/* Price — anchors future value */}
        {price && <div className="mt-auto pt-3" data-id="element-307">
            <p className="text-xs tracking-[0.25em] uppercase text-gray-500 mb-1 font-serif" data-id="element-308">
              Valor del Archivo
            </p>
            <p className="text-2xl font-serif font-bold opacity-70" style={{
          color: signColor,
          textShadow: `0 0 12px ${signColor}50`
        }} data-id="element-309">
              {price}
            </p>
          </div>}
      </div>

      {/* Hover energy border */}
      <motion.div className="absolute inset-0 rounded-xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
      border: `1px solid ${signColor}`,
      boxShadow: `inset 0 0 40px ${signColor}30, 0 0 30px ${signColor}50`
    }} data-id="element-310" />
    </motion.div>;
}
/** Default celestial sigil — concentric runic circle with star */
function DefaultSigil({
  color
}: {
  color: string;
}) {
  return <svg width="44" height="44" viewBox="0 0 44 44" fill="none" data-id="element-311">
      <circle cx="22" cy="22" r="20" stroke={color} strokeWidth="0.8" opacity="0.6" data-id="element-312" />
      <circle cx="22" cy="22" r="14" stroke={color} strokeWidth="0.5" strokeDasharray="2 3" opacity="0.7" data-id="element-313" />
      <circle cx="22" cy="22" r="3" fill={color} opacity="0.9" data-id="element-314" />
      {/* Eight-point star */}
      {[0, 45, 90, 135].map(angle => <line key={angle} x1="22" y1="6" x2="22" y2="38" stroke={color} strokeWidth="0.6" opacity="0.5" transform={`rotate(${angle} 22 22)`} data-id="element-315" />)}
      {/* Outer rune ticks */}
      {[0, 60, 120, 180, 240, 300].map(angle => <line key={`tick-${angle}`} x1="22" y1="2" x2="22" y2="5" stroke={color} strokeWidth="1" transform={`rotate(${angle} 22 22)`} data-id="element-316" />)}
    </svg>;
}